"use strict";
import { Token } from "@uniswap/sdk-core";
import { useReadContracts } from "wagmi";
import { isAddress } from "utilities/src/addresses";
import { useMemo } from "react";
import { SafetyLevel, ProtectionResult } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { getCurrencySafetyInfo } from "features/dataApi/utils";
import { getTokenLogoUrl } from "constants/tokenLogos";
import { currencyId } from "utils/uniswapCurrencyId";
const ERC20_METADATA_ABI = [
  {
    name: "name",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "string" }]
  },
  {
    name: "symbol",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "string" }]
  },
  {
    name: "decimals",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint8" }]
  },
  {
    name: "totalSupply",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }]
  }
];
export function useTokenFromAddress(address, chainId) {
  const validAddress = useMemo(() => {
    return address ? isAddress(address) : false;
  }, [address]);
  const {
    data: contractData,
    isLoading: contractLoading,
    error: contractError
  } = useReadContracts({
    contracts: validAddress ? [
      {
        address: validAddress,
        abi: ERC20_METADATA_ABI,
        functionName: "name",
        chainId
      },
      {
        address: validAddress,
        abi: ERC20_METADATA_ABI,
        functionName: "symbol",
        chainId
      },
      {
        address: validAddress,
        abi: ERC20_METADATA_ABI,
        functionName: "decimals",
        chainId
      },
      {
        address: validAddress,
        abi: ERC20_METADATA_ABI,
        functionName: "totalSupply",
        chainId
      }
    ] : void 0,
    query: {
      enabled: !!validAddress,
      staleTime: 1e3 * 60 * 60,
      // 1 hour - token metadata rarely changes
      refetchOnMount: false,
      refetchOnWindowFocus: false
    }
  });
  const result = useMemo(() => {
    if (!validAddress) {
      return {
        data: null,
        loading: false,
        error: "Invalid address",
        isValidToken: false
      };
    }
    if (contractLoading) {
      return {
        data: null,
        loading: true,
        error: null,
        isValidToken: false
      };
    }
    if (contractError) {
      return {
        data: null,
        loading: false,
        error: "Failed to fetch token data",
        isValidToken: false
      };
    }
    if (!contractData || contractData.some((result2) => result2.status === "failure")) {
      const isNotContract = contractData?.some(
        (result2) => result2.status === "failure" && result2.error?.message?.includes("address is not a contract")
      );
      const errorMessage = isNotContract ? "Address is not a valid contract" : "Not a valid ERC20 token";
      return {
        data: null,
        loading: false,
        error: errorMessage,
        isValidToken: false
      };
    }
    const [nameResult, symbolResult, decimalsResult, totalSupplyResult] = contractData;
    if (nameResult.status === "success" && symbolResult.status === "success" && decimalsResult.status === "success" && totalSupplyResult.status === "success") {
      const name = nameResult.result;
      const symbol = symbolResult.result;
      const decimals = decimalsResult.result;
      const _totalSupply = totalSupplyResult.result;
      if (!name || !symbol || decimals > 18 || decimals < 0) {
        return {
          data: null,
          loading: false,
          error: "Invalid token metadata",
          isValidToken: false
        };
      }
      try {
        const token = new Token(chainId, validAddress, decimals, symbol, name);
        const safetyInfo = getCurrencySafetyInfo(SafetyLevel.MediumWarning, {
          attackTypes: [],
          result: ProtectionResult.Unknown
        });
        const currencyInfo = {
          currencyId: currencyId(token),
          currency: token,
          logoUrl: getTokenLogoUrl(validAddress),
          safetyLevel: SafetyLevel.MediumWarning,
          safetyInfo,
          isSpam: false
        };
        return {
          data: currencyInfo,
          loading: false,
          error: null,
          isValidToken: true
        };
      } catch (error) {
        return {
          data: null,
          loading: false,
          error: "Failed to create token",
          isValidToken: false
        };
      }
    }
    return {
      data: null,
      loading: false,
      error: "Incomplete token data",
      isValidToken: false
    };
  }, [validAddress, contractLoading, contractError, contractData, chainId]);
  return result;
}
